import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  People,
  School,
  Assignment,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import { useQueryClient } from '@tanstack/react-query';
import api from "../../services/api";

const Department = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : {};
  });

  // Handle export functionality
  const handleExport = async () => {
    if (!currentUser?.instituteId) {
      console.error("User not authenticated or instituteId missing");
      return;
    }

    try {
      const endpoint = activeTab === 0
        ? "/teacher/export-csv-hod-teachers"
        : "/teacher/export-csv-hod-classes";

      const response = await api.post(endpoint, {
        instituteId: currentUser.instituteId,
        departmentId: currentUser.departmentId,
      }, { responseType: 'blob' });

      if (response.status !== 200) {
        throw new Error('Export failed');
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = activeTab === 0 ? 'department_teachers.csv' : 'department_classes.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export error:", error);
      // You might want to show a toast notification here
    }
  };
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    headName: "",
  });

  // Fetch department info on mount
  useEffect(() => {
    const fetchDepartmentInfo = async () => {
      try {
        const response = await api.get('/department-info/details');
        if (response.data.status === 200) {
          const data = response.data.data;
          setDepartmentInfo({
            name: data.department?.name || "Department",
            code: data.department?.code || "",
            head: data.hod?.fullName || "Not Assigned",
            established: "N/A", // Not available in API
            totalTeachers: data.teacherCount || 0,
            totalStudents: data.studentCount || 0,
            totalCourses: data.courseCount || 0,
            description: data.department?.description || "",
          });
        }
      } catch (error) {
        console.error("Error fetching department info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentInfo();
  }, []);

  const departmentInfoDefault = {
    name: "Computer Science",
    code: "CS",
    head: "Dr. Robert Wilson",
    established: "2010",
    totalTeachers: 15,
    totalStudents: 450,
    totalCourses: 8,
    description:
      "Department of Computer Science and Engineering focuses on software development, AI, and data science.",
  };

  const teachers = [
    {
      id: 1,
      name: "Dr. Robert Wilson",
      designation: "Professor & HOD",
      email: "robert@school.edu",
      phone: "9876543210",
      subjects: ["Data Structures", "Algorithms"],
    },
    {
      id: 2,
      name: "Prof. Sarah Johnson",
      designation: "Associate Professor",
      email: "sarah@school.edu",
      phone: "9876543211",
      subjects: ["Machine Learning", "AI"],
    },
    {
      id: 3,
      name: "Dr. Mike Brown",
      designation: "Assistant Professor",
      email: "mike@school.edu",
      phone: "9876543212",
      subjects: ["Database", "Web Development"],
    },
  ];

  const courses = [
    {
      id: 1,
      name: "B.Tech Computer Science",
      code: "BTCS",
      students: 120,
      duration: "4 years",
    },
    {
      id: 2,
      name: "M.Tech Computer Science",
      code: "MTCS",
      students: 60,
      duration: "2 years",
    },
    { id: 3, name: "BCA", code: "BCA", students: 180, duration: "3 years" },
  ];

  const teacherColumns = [
    {
      field: "name",
      headerName: "Teacher",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>{params.row.name[0]}</Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.designation}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", width: 180 },
    { field: "phone", headerName: "Phone", width: 130 },
    {
      field: "subjects",
      headerName: "Subjects",
      flex: 1,
      renderCell: (params) =>
        params.row.subjects.map((s, i) => (
          <Chip key={i} label={s} size="small" sx={{ mr: 0.5 }} />
        )),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: () => (
        <Box>
          <IconButton size="small">
            <Visibility />
          </IconButton>
          <IconButton size="small">
            <Edit />
          </IconButton>
        </Box>
      ),
    },
  ];

  const courseColumns = [
    { field: "name", headerName: "Course Name", flex: 1 },
    { field: "code", headerName: "Code", width: 100 },
    { field: "students", headerName: "Students", width: 100 },
    { field: "duration", headerName: "Duration", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: () => (
        <Box>
          <IconButton size="small">
            <Visibility />
          </IconButton>
          <IconButton size="small">
            <Edit />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Department Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your department details
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit Details
            </Button>
          </Box>

          {/* Department Info Card */}
          <Card
            sx={{
              mb: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h4" fontWeight={700}>
                    {(departmentInfo || departmentInfoDefault).name}
                  </Typography>
                  <Chip
                    label={(departmentInfo || departmentInfoDefault).code || "N/A"}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", mt: 1 }}
                  />
                  <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                    {(departmentInfo || departmentInfoDefault).description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Head of Department: <strong>{(departmentInfo || departmentInfoDefault).head}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Established: <strong>{(departmentInfo || departmentInfoDefault).established}</strong>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Grid container spacing={2}>
                    {[
                      {
                        label: "Teachers",
                        value: (departmentInfo || departmentInfoDefault).totalTeachers,
                        icon: <People />,
                      },
                      {
                        label: "Students",
                        value: (departmentInfo || departmentInfoDefault).totalStudents,
                        icon: <School />,
                      },
                      {
                        label: "Courses",
                        value: (departmentInfo || departmentInfoDefault).totalCourses,
                        icon: <Assignment />,
                      },
                    ].map((stat, i) => (
                      <Grid item xs={4} key={i}>
                        <Box
                          sx={{
                            textAlign: "center",
                            bgcolor: "rgba(255,255,255,0.1)",
                            borderRadius: 2,
                            p: 2,
                          }}
                        >
                          {stat.icon}
                          <Typography variant="h5" fontWeight={700}>
                            {stat.value}
                          </Typography>
                          <Typography variant="caption">{stat.label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="Teachers" />
            <Tab label="Courses" />
          </Tabs>

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <TextField
              placeholder="Search..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
              >
                Import
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<Download />}
                onClick={handleExport}
                disabled={!currentUser?.instituteId}
              >
                Export
              </Button>
              <Button variant="contained" startIcon={<Add />}>
                Add {activeTab === 0 ? "Teacher" : "Course"}
              </Button>
            </Box>
          </Box>

          <DataGrid
            rows={activeTab === 0 ? teachers : courses}
            columns={activeTab === 0 ? teacherColumns : courseColumns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setEditDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Edit Department Details
            <IconButton onClick={() => setEditDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Department Name"
                defaultValue={departmentInfo.name}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Code"
                defaultValue={departmentInfo.code}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Head of Department"
                defaultValue={departmentInfo.head}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                defaultValue={departmentInfo.description}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType={activeTab === 0 ? "teachers" : "classes"}
        instituteId={currentUser?.instituteId || ""}
        departmentId={currentUser?.departmentId || ""}
        onImportComplete={() => {
          // Invalidate relevant queries to refresh the list
          if (activeTab === 0) {
            queryClient.invalidateQueries({ queryKey: ["department-teachers"] });
          } else {
            queryClient.invalidateQueries({ queryKey: ["department-classes"] });
          }
        }}
      />
        </>
      )}
    </Box>
  );
};

export default Department;
